// src/pages/FFTTPage.js — Scraper spid.fftt.fr · Cibler ses proies
import React, { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { Link } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import "./FFTTPage.css";

// Proxy CORS — en prod utiliser ton serveur Node comme proxy
// Le serveur doit avoir une route GET /fftt/search?q=nom&club=club
const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

export default function FFTTPage() {
  const { user, profile } = useAuth();
  const [nom, setNom]       = useState("");
  const [club, setClub]     = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [preys, setPreys]   = useState(() => {
    try { return JSON.parse(localStorage.getItem("takkyu_preys") || "[]"); }
    catch { return []; }
  });

  const search = async () => {
    if (!nom.trim()) return;
    setLoading(true); setError(null); setResults([]);
    try {
      const params = new URLSearchParams({ q: nom.trim() });
      if (club.trim()) params.append("club", club.trim());
      const res = await fetch(`${API}/fftt/search?${params}`);
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setResults(data.players || []);
    } catch(e) {
      setError("Erreur de connexion au registre FFTT. Vérifie que ton serveur tourne et que la route /fftt/search existe.");
      console.error(e);
    }
    setLoading(false);
  };

  const addPrey = (player) => {
    const updated = [...preys.filter(p => p.licence !== player.licence), {
      ...player, addedAt: new Date().toISOString()
    }].slice(0, 20);
    setPreys(updated);
    localStorage.setItem("takkyu_preys", JSON.stringify(updated));
  };

  const removePrey = (licence) => {
    const updated = preys.filter(p => p.licence !== licence);
    setPreys(updated);
    localStorage.setItem("takkyu_preys", JSON.stringify(updated));
  };

  const getOniComment = (points) => {
    if (points >= 1800) return { label:"BOSS FINAL", color:"var(--blood)", icon:"💀" };
    if (points >= 1500) return { label:"ADVERSAIRE SÉRIEUX", color:"var(--ember)", icon:"⚠️" };
    if (points >= 1200) return { label:"PROIE POSSIBLE", color:"var(--gold)", icon:"🎯" };
    return { label:"PROIE FACILE", color:"#4a9e6b", icon:"🦴" };
  };

  const myPoints = 1330; // TODO: lier au profil utilisateur si classement renseigné

  return (
    <div className="fftt-page">
      <div className="section" style={{paddingTop:"7rem"}}>

        <div className="fftt-header">
          <span className="section-eyebrow">FFTT · 獲物</span>
          <h1 className="section-title">Cibler<br/>ses Proies</h1>
          <div className="divider"/>
          <p className="section-sub">
            Recherche dans le registre officiel FFTT. Identifie tes adversaires. Ajoute-les à ta liste de proies.
          </p>
          <div className="fftt-disclaimer">
            ⚠️ Données issues de spid.fftt.fr via ton serveur Node. Non officiel — peut être fragile.
          </div>
        </div>

        <div className="fftt-layout">

          {/* Search */}
          <div className="fftt-main">
            <div className="card fftt-search-card">
              <p className="fftt-card-title">RECHERCHE</p>
              <div className="fftt-search-row">
                <div className="field" style={{flex:2}}>
                  <label>NOM DU JOUEUR</label>
                  <input type="text" value={nom} onChange={e=>setNom(e.target.value)}
                    placeholder="Ex: Dupont..." onKeyDown={e=>e.key==="Enter"&&search()}/>
                </div>
                <div className="field" style={{flex:1}}>
                  <label>CLUB (optionnel)</label>
                  <input type="text" value={club} onChange={e=>setClub(e.target.value)}
                    placeholder="Ex: Paris..." onKeyDown={e=>e.key==="Enter"&&search()}/>
                </div>
                <button className="btn btn-primary btn-sm fftt-search-btn"
                  onClick={search} disabled={loading||!nom.trim()}>
                  {loading ? <><span className="spinner"/> SCAN...</> : "SCANNER ↗"}
                </button>
              </div>

              {error && <div className="fftt-error">{error}</div>}

              {/* Results */}
              {results.length > 0 && (
                <div className="fftt-results">
                  <p className="fftt-results-count">{results.length} joueur{results.length>1?"s":""} trouvé{results.length>1?"s":""}</p>
                  {results.map((player, i) => {
                    const oni = getOniComment(parseInt(player.points)||0);
                    const isPrey = preys.some(p=>p.licence===player.licence);
                    return (
                      <div key={i} className="fftt-player-row">
                        <div className="fftt-player-main">
                          <div className="fftt-player-identity">
                            <p className="fftt-player-name">{player.nom} {player.prenom}</p>
                            <p className="fftt-player-club">{player.club}</p>
                            <p className="fftt-player-dept">Dép. {player.departement} · Lic. {player.licence}</p>
                          </div>
                          <div className="fftt-player-stats">
                            <div className="fftt-points">
                              <span className="fftt-points-val">{player.points || "—"}</span>
                              <span className="fftt-points-label">pts</span>
                            </div>
                            <span className="fftt-oni-tag" style={{color:oni.color, borderColor:`${oni.color}40`}}>
                              {oni.icon} {oni.label}
                            </span>
                          </div>
                        </div>
                        <div className="fftt-player-actions">
                          <a
                            href={`https://www.fftt.com/site/classement/index.php?licence=${player.licence}`}
                            target="_blank" rel="noreferrer"
                            className="btn btn-ghost btn-sm"
                          >
                            FICHE FFTT ↗
                          </a>
                          <button
                            className={`btn btn-sm${isPrey ? " btn-ghost fftt-remove" : " btn-primary"}`}
                            onClick={() => isPrey ? removePrey(player.licence) : addPrey(player)}
                          >
                            {isPrey ? "RETIRER" : "+ PROIE"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {results.length === 0 && !loading && nom.length > 0 && (
                <p className="fftt-no-results">Lance la recherche pour scanner le registre.</p>
              )}
            </div>

            {/* Server setup notice */}
            <div className="fftt-setup-notice card">
              <p className="fftt-card-title">CONFIGURATION SERVEUR REQUISE</p>
              <p className="fftt-setup-text">
                Ajoute cette route dans ton serveur Node.js (<code>server.js</code>) :
              </p>
              <pre className="fftt-code">{`// Route FFTT scraper
const axios = require('axios');
const cheerio = require('cheerio');

app.get('/fftt/search', async (req, res) => {
  const { q, club } = req.query;
  try {
    const url = \`https://spid.fftt.com/Spid/joueur/recherche?nom=\${encodeURIComponent(q)}\${club ? \`&club=\${encodeURIComponent(club)}\` : ''}\`;
    const { data } = await axios.get(url, {
      headers: { 'User-Agent': 'Mozilla/5.0' }
    });
    const $ = cheerio.load(data);
    const players = [];
    $('table tbody tr').each((i, el) => {
      const cols = $(el).find('td');
      if (cols.length >= 5) {
        players.push({
          licence:     $(cols[0]).text().trim(),
          nom:         $(cols[1]).text().trim(),
          prenom:      $(cols[2]).text().trim(),
          club:        $(cols[3]).text().trim(),
          departement: $(cols[4]).text().trim(),
          points:      $(cols[5]).text().trim(),
        });
      }
    });
    res.json({ players });
  } catch(err) {
    res.status(500).json({ error: err.message });
  }
});`}</pre>
              <p className="fftt-setup-sub">Installe : <code>npm install axios cheerio</code></p>
            </div>
          </div>

          {/* Prey list */}
          <div className="fftt-side">
            <div className="card fftt-preys-card">
              <p className="fftt-card-title">MES PROIES · {preys.length}</p>
              {preys.length === 0 ? (
                <EmptyState type="fftt_no_preys" style={{padding:"1.5rem"}}/>
              ) : (
                <div className="fftt-preys-list">
                  {preys.map((player, i) => {
                    const oni = getOniComment(parseInt(player.points)||0);
                    return (
                      <div key={i} className="fftt-prey-item">
                        <div className="fftt-prey-left">
                          <span className="fftt-prey-icon">{oni.icon}</span>
                          <div>
                            <p className="fftt-prey-name">{player.nom} {player.prenom}</p>
                            <p className="fftt-prey-info">{player.points} pts · {player.club}</p>
                          </div>
                        </div>
                        <button className="fftt-prey-remove" onClick={() => removePrey(player.licence)} title="Retirer">✕</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {preys.length > 0 && (
                <div className="fftt-preys-stats">
                  <p className="fftt-card-title" style={{marginBottom:".5rem"}}>ANALYSE</p>
                  <p className="fftt-preys-stat">
                    Moyenne : <strong>{Math.round(preys.reduce((s,p)=>s+(parseInt(p.points)||0),0)/preys.length)} pts</strong>
                  </p>
                  <p className="fftt-preys-stat">
                    Plus fort : <strong>{Math.max(...preys.map(p=>parseInt(p.points)||0))} pts</strong>
                  </p>
                  <p className="fftt-preys-stat">
                    Plus faible : <strong>{Math.min(...preys.map(p=>parseInt(p.points)||0))} pts</strong>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
